import React, { useMemo } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { COMPLIANCE_META, isComplianceStatsFocus } from '../complianceFocus';
import { buildComplianceCountRows } from '../complianceCount';
import type { CrewStatisticsResponse } from '../types';
import { ComplianceCountReport } from './ComplianceCountReport';
import { complianceCountBackPath, complianceCountListApiUrl } from './complianceCountPrintMeta';

const ComplianceCountReportPage: React.FC = () => {
  const { colors } = useTheme();
  const { focus: focusParam } = useParams<{ focus?: string }>();
  const [searchParams] = useSearchParams();
  const focus = isComplianceStatsFocus(focusParam) ? focusParam : null;

  const listUrl = useMemo(() => complianceCountListApiUrl(searchParams), [searchParams]);
  const { data, error, isLoading } = useSWR<CrewStatisticsResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  if (!focus) {
    return <Navigate to="/crew/statistics/compliance/certs-expired" replace />;
  }

  const meta = COMPLIANCE_META[focus];
  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const withinDays = searchParams.get('withinDays')?.trim() || String(data?.withinDays ?? 90);

  const vesselLabel = data?.vessel?.vesselName
    ? data.vessel.vesselName
    : !vesselId
      ? 'All vessels'
      : `Vessel #${vesselId}`;

  const lists = data?.lists;
  const certs = focus === 'certs-expired' ? lists?.certsExpired ?? [] : lists?.certsExpiring ?? [];
  const medicals =
    focus === 'medicals-expired' ? lists?.medicalsExpired ?? [] : lists?.medicalsExpiring ?? [];
  const rows = buildComplianceCountRows(focus, { certs, medicals });
  const recordCount =
    focus === 'certs-expired' || focus === 'certs-expiring' ? certs.length : medicals.length;

  const backTo = complianceCountBackPath(focus);

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading {meta.title.toLowerCase()} counts…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load {meta.title.toLowerCase()} statistics.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to {meta.title}
        </Link>
      </div>
    );
  }

  return (
    <ReportView
      backTo={backTo}
      backLabel={`Back to ${meta.title}`}
      title={`${meta.title} counts`}
      subtitle={`${vesselLabel} · ${recordCount} ${meta.detailTab.toLowerCase()}`}
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <ComplianceCountReport
        focus={focus}
        rows={rows}
        vesselLabel={vesselLabel}
        recordCount={recordCount}
        withinDays={withinDays}
      />
    </ReportView>
  );
};

export default ComplianceCountReportPage;
